import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { 
  Truck, 
  Users, 
  MapPin, 
  Fuel, 
  Wrench, 
  AlertTriangle, 
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Radio,
  Route,
  Map,
  Shield,
  Send,
  ShoppingBag
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFleetAlerts } from "@/hooks/use-fleet-alerts";
import { useFleetCostsSummary } from "@/hooks/use-fleet-costs";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Separator } from "@/components/ui/separator";
import { useHasGpsTracking } from "@/hooks/useHasGpsTracking";
import { useGpsStats } from "@/hooks/useGpsStats";
import { Link } from "react-router-dom";

import { WorkOrderFormSheet } from "@/components/fleet/WorkOrderFormSheet";
import { VehicleFormSheet } from "@/components/fleet/VehicleFormSheet";
import { DriverFormSheet } from "@/components/fleet/DriverFormSheet";
import { TripFormSheet } from "@/components/fleet/TripFormSheet";

export default function FleetDashboard() {
  const { profile } = useAuth();
  const { canCreate, isViewer } = useFleetPermissions();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  
  // States for modals
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showTripModal, setShowTripModal] = useState(false);

  // Get current user's companies
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;
  
  // Check if company has GPS tracking
  const { hasGps } = useHasGpsTracking(currentCompanyId);
  const gpsStats = useGpsStats(currentCompanyId);

  // Fleet vehicles data
  const { data: vehicles = [] } = useQuery({
    queryKey: ["fleetVehicles", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_vehicles")
        .select("*")
        .eq("company_id", currentCompanyId);
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Fleet drivers data
  const { data: drivers = [] } = useQuery({
    queryKey: ["fleetDrivers", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_drivers")
        .select("*")
        .eq("company_id", currentCompanyId);
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Fleet trips data
  const { data: trips = [] } = useQuery({
    queryKey: ["fleetTrips", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_trips")
        .select("*, fleet_vehicles(plate), fleet_drivers(name)")
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Fleet fuel logs for recent activity
  const { data: fuelLogs = [] } = useQuery({
    queryKey: ["fleetFuelLogs", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data } = await supabase
        .from("fleet_fuel_logs")
        .select("*, fleet_vehicles(plate)")
        .eq("company_id", currentCompanyId)
        .gte("occurred_at", today.toISOString())
        .order("occurred_at", { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Fleet work orders for recent activity
  const { data: workOrders = [] } = useQuery({
    queryKey: ["fleetWorkOrders", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_work_orders")
        .select("*, fleet_vehicles(plate)")
        .eq("company_id", currentCompanyId)
        .eq("status", "closed")
        .order("closed_at", { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Fleet performance metrics
  const { data: performanceData } = useQuery({
    queryKey: ["fleetPerformance", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return null;
      
      // Get current month fuel consumption
      const currentMonth = new Date();
      currentMonth.setDate(1);
      const nextMonth = new Date(currentMonth);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      const { data: fuelData } = await supabase
        .from("fleet_fuel_logs")
        .select("liters, price_unit")
        .eq("company_id", currentCompanyId)
        .gte("occurred_at", currentMonth.toISOString())
        .lt("occurred_at", nextMonth.toISOString());
      
      const { data: workOrderData } = await supabase
        .from("fleet_work_orders")
        .select("total_cost")
        .eq("company_id", currentCompanyId)
        .gte("opened_at", currentMonth.toISOString())
        .lt("opened_at", nextMonth.toISOString());
      
      return { fuelData: fuelData || [], workOrderData: workOrderData || [] };
    },
    enabled: !!currentCompanyId
  });

  // Use fleet alerts hook
  const { data: alertsData = [] } = useFleetAlerts(currentCompanyId);
  
  // Use fleet costs summary hook
  const { data: costsSummary } = useFleetCostsSummary(currentCompanyId);

  // Data for work order modal
  const { data: providers = [] } = useQuery({
    queryKey: ["fleetProviders", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_providers")
        .select("*")
        .eq("company_id", currentCompanyId);
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Calculate KPIs
  const activeVehicles = vehicles.filter(v => v.status === 'active').length;
  const maintenanceVehicles = vehicles.filter(v => v.status === 'maintenance').length;
  const activeDrivers = drivers.filter(d => d.status === 'active').length;
  const inProgressTrips = trips.filter(t => t.status === 'in_progress').length;

  const kpis = [
    {
      title: "Veículos Ativos",
      value: activeVehicles,
      total: vehicles.length,
      icon: Truck,
      color: "text-green-600",
      bgColor: "bg-green-50 dark:bg-green-900/20"
    },
    {
      title: "Em Manutenção",
      value: maintenanceVehicles,
      total: vehicles.length,
      icon: Wrench,
      color: "text-orange-600",
      bgColor: "bg-orange-50 dark:bg-orange-900/20"
    },
    {
      title: "Motoristas Ativos",
      value: activeDrivers,
      total: drivers.length,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-900/20"
    },
    {
      title: "Viagens em Andamento",
      value: inProgressTrips,
      total: trips.length,
      icon: MapPin,
      color: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-900/20"
    }
  ];

  // Transform alerts data
  const alerts = alertsData.slice(0, 3).map(alert => ({
    type: alert.priority === 'high' ? 'error' : alert.priority === 'medium' ? 'warning' : 'info',
    icon: alert.priority === 'high' ? XCircle : alert.priority === 'medium' ? AlertTriangle : Clock,
    title: alert.title,
    message: alert.description || 'Sem descrição',
    time: format(new Date(alert.triggered_at), 'HH:mm', { locale: ptBR }),
    vehiclePlate: alert.fleet_vehicles?.plate,
    driverName: alert.fleet_drivers?.name
  }));

  // Combine recent activities from different sources
  const recentActivity = [
    // Recent trips
    ...trips.slice(0, 2).map(trip => ({
      type: "trip",
      icon: MapPin,
      title: trip.status === 'in_progress' ? 'Viagem em andamento' : 'Viagem iniciada',
      description: `${trip.fleet_vehicles?.plate} - ${trip.origin || 'Origem'} → ${trip.destination || 'Destino'}`,
      time: format(new Date(trip.start_at || trip.created_at), 'HH:mm', { locale: ptBR }),
      status: trip.status
    })),
    // Recent fuel logs
    ...fuelLogs.slice(0, 2).map(fuel => ({
      type: "fuel",
      icon: Fuel,
      title: "Abastecimento registrado",
      description: `${fuel.fleet_vehicles?.plate} - ${fuel.liters}L - ${formatCurrency(fuel.liters * (fuel.price_unit || 0))}`,
      time: format(new Date(fuel.occurred_at), 'HH:mm', { locale: ptBR }),
      status: "completed"
    })),
    // Recent work orders
    ...workOrders.slice(0, 1).map(order => ({
      type: "maintenance",
      icon: Wrench,
      title: "Manutenção concluída",
      description: `${order.fleet_vehicles?.plate} - ${order.description || 'Serviços realizados'}`,
      time: format(new Date(order.closed_at), 'HH:mm', { locale: ptBR }),
      status: "completed"
    }))
  ].sort((a, b) => b.time.localeCompare(a.time)).slice(0, 5);

  // Calculate performance metrics
  const calculatePerformance = () => {
    if (!performanceData) return { avgConsumption: 0, avgCostPerKm: 0, fleetUtilization: 0 };
    
    const totalFuel = performanceData.fuelData.reduce((sum, log) => sum + log.liters, 0);
    const totalFuelCost = performanceData.fuelData.reduce((sum, log) => sum + (log.liters * (log.price_unit || 0)), 0);
    const totalMaintenanceCost = performanceData.workOrderData.reduce((sum, order) => sum + (order.total_cost || 0), 0);
    
    // These are simplified calculations - in reality you'd need more complex logic
    const avgConsumption = totalFuel > 0 ? (totalFuel / Math.max(vehicles.length, 1) * 10).toFixed(1) : 0;
    const avgCostPerKm = totalFuel > 0 ? ((totalFuelCost + totalMaintenanceCost) / (totalFuel * 10)).toFixed(2) : 0;
    const fleetUtilization = vehicles.length > 0 ? Math.round((inProgressTrips / vehicles.length) * 100) : 0;
    
    return { avgConsumption, avgCostPerKm, fleetUtilization };
  };

  const performance = calculatePerformance();

  // Handler removido conforme guidelines - relatórios acessados apenas via menu /fleet/reports

  // Quick action handlers
  const handleCreateVehicle = () => {
    setShowVehicleModal(true);
  };

  const handleCreateDriver = () => {
    setShowDriverModal(true);
  };

  const handleCreateTrip = () => {
    setShowTripModal(true);
  };

  const handleScheduleMaintenance = () => {
    setShowWorkOrderModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard da Frota</h1>
          <p className="text-muted-foreground">
            Visão geral das operações em tempo real
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Badge variant="outline">
            <CheckCircle className="w-3 h-3 mr-1" />
            Sistema Online
          </Badge>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <Card key={kpi.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {kpi.title}
              </CardTitle>
              <div className={`p-2 rounded-md ${kpi.bgColor}`}>
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpi.value}</div>
              <p className="text-xs text-muted-foreground">
                de {kpi.total} total
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* GPS Section */}
      {hasGps && (
        <>
          <Separator className="my-6" />
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Radio className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-bold">Monitoramento GPS</h2>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Veículos Online
                  </CardTitle>
                  <Radio className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{gpsStats.online}</div>
                  <p className="text-xs text-muted-foreground">
                    de {gpsStats.total} com GPS
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Viagens Hoje
                  </CardTitle>
                  <Route className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{gpsStats.tripsToday}</div>
                  <p className="text-xs text-muted-foreground">
                    {gpsStats.percentChange > 0 ? '+' : ''}{gpsStats.percentChange}% vs ontem
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Alertas GPS Ativos
                  </CardTitle>
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{gpsStats.activeAlerts}</div>
                  <p className="text-xs text-muted-foreground">
                    Velocidade, geocerca, bateria
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Km Rodados Hoje
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {gpsStats.kmToday.toLocaleString()} km
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Toda a frota
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* GPS Quick Access Buttons */}
            <div className="grid gap-2 md:grid-cols-4">
              <Button variant="outline" className="justify-start" asChild>
                <Link to={`/company/${slug}/admin/systems/fleet/monitoring/live`}>
                  <Map className="h-4 w-4 mr-2" />
                  Mapa ao Vivo
                </Link>
              </Button>
              <Button variant="outline" className="justify-start" asChild>
                <Link to={`/company/${slug}/admin/systems/fleet/monitoring/geofences`}>
                  <Shield className="h-4 w-4 mr-2" />
                  Geocercas
                </Link>
              </Button>
              <Button variant="outline" className="justify-start" asChild>
                <Link to={`/company/${slug}/admin/systems/fleet/monitoring/alerts`}>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Alertas GPS
                </Link>
              </Button>
              <Button variant="outline" className="justify-start" asChild>
                <Link to={`/company/${slug}/admin/systems/fleet/store`}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Loja de Hardware
                </Link>
              </Button>
            </div>
          </div>
        </>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Alerts */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alertas Recentes
            </CardTitle>
            <CardDescription>
              Notificações importantes da sua frota
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {alerts.length > 0 ? (
                alerts.map((alert, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg border">
                    <div className={`p-1 rounded ${
                      alert.type === 'error' ? 'bg-red-100 text-red-600' :
                      alert.type === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      <alert.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{alert.title}</p>
                      <p className="text-sm text-muted-foreground">{alert.message}</p>
                      {alert.vehiclePlate && (
                        <p className="text-xs text-muted-foreground">Veículo: {alert.vehiclePlate}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{alert.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum alerta recente</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
            <CardDescription>
              Últimas movimentações da frota
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted">
                      <activity.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.title}</p>
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {activity.time}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma atividade recente</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Performance
            </CardTitle>
            <CardDescription>
              Indicadores do mês atual
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Consumo médio</span>
                <span className="font-medium">{performance.avgConsumption} km/L</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <TrendingUp className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Baseado no mês atual</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Custo/km médio</span>
                <span className="font-medium">R$ {performance.avgCostPerKm}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <TrendingDown className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Combustível + manutenção</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Utilização da frota</span>
                <span className="font-medium">{performance.fleetUtilization}%</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <TrendingUp className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Veículos em viagem</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleCreateVehicle}
            >
              <Truck className="w-4 h-4 mr-2" />
              Cadastrar Veículo
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleCreateDriver}
            >
              <Users className="w-4 h-4 mr-2" />
              Cadastrar Motorista
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleCreateTrip}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Nova Viagem
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={handleScheduleMaintenance}
            >
              <Wrench className="w-4 h-4 mr-2" />
              Agendar Manutenção
            </Button>
          </CardContent>
        </Card>

        {/* Cost Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Resumo de Custos
            </CardTitle>
            <CardDescription>
              Gastos do mês atual
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {costsSummary ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-blue-500" />
                    <span className="text-sm">Combustível</span>
                  </div>
                  <span className="font-medium">{formatCurrency(costsSummary.fuelCost || 0)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-orange-500" />
                    <span className="text-sm">Manutenção</span>
                  </div>
                  <span className="font-medium">{formatCurrency(costsSummary.maintenanceCost || 0)}</span>
                </div>

                <div className="pt-3 border-t">
                  <div className="flex justify-between items-center font-medium">
                    <span>Total</span>
                    <span>{formatCurrency(costsSummary.totalCost)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Carregando custos...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      {currentCompanyId && (
        <>
          <WorkOrderFormSheet
            open={showWorkOrderModal}
            onOpenChange={setShowWorkOrderModal}
            onSubmit={async (workOrderData) => {
              try {
                const { error } = await supabase
                  .from("fleet_work_orders")
                  .insert({
                    ...workOrderData,
                    company_id: currentCompanyId,
                    opened_at: new Date().toISOString(),
                    status: 'open'
                  });
                
                if (error) throw error;
                setShowWorkOrderModal(false);
              } catch (error) {
                console.error('Erro ao criar ordem de serviço:', error);
              }
            }}
            vehicles={vehicles}
            providers={providers}
            maintenancePlans={[]}
            isLoading={false}
          />

          <VehicleFormSheet
            open={showVehicleModal}
            onOpenChange={setShowVehicleModal}
            companyId={currentCompanyId}
            onSubmit={() => {}}
          />

          <DriverFormSheet
            open={showDriverModal}
            onOpenChange={setShowDriverModal}
            companyId={currentCompanyId}
          />

          <TripFormSheet
            open={showTripModal}
            onOpenChange={setShowTripModal}
            companyId={currentCompanyId}
          />
        </>
      )}
    </div>
  );
}